// URLPeekData import removed - no longer needed after Phase 6 migration
import { apiSSE } from '@lib/network/api';
import { getPlatformConfig } from '@lib/utils/platforms';
import { safeEncodeURI } from '@lib/utils/url';
import {
  buildGoogleDriveFolderData,
  isGoogleDriveFolderMetadata,
  type GoogleDriveFolderData,
  type GoogleDriveFolderMetadata,
} from './googleDriveFolder';

// Re-export the Drive-folder types/guard so consumers can import everything
// unfurl-related from one place, mirroring the other payload types.
export {
  isGoogleDriveFolderData,
  type GoogleDriveFolderData,
  type GoogleDriveFolderEntity,
  type GoogleDriveFolderMetadata,
} from './googleDriveFolder';

/**
 * Interface for a successful API response.
 * @template T The type of the data payload.
 */
export interface SuccessResponse<T> {
  success: true;
  data: T;
}

/**
 * Interface for an error API response.
 */
export interface ErrorResponse {
  success: false;
  error: {
    statusCode?: number;
    error: string;
    message: string;
    code?: string;
    validation?: {
      source: string;
      keys: string[];
    };
  };
  status: 'error';
}

/**
 * Common column definitions for various media types.
 */
export interface ColumnDefinition {
  key: string;
  type: 'thumbnail' | 'string' | 'number' | 'ISODateString' | 'boolean' | 'enum';
  name: string;
  description: string;
  defaultHidden?: boolean;
  /**
   * Only applicable when `type` is "enum".
   */
  choices?: { key: string; value: string }[];
}

/**
 * Maps a ColumnDefinition type string to its corresponding TypeScript type.
 */
type ColumnTypeMap = {
  thumbnail: string;
  string: string;
  number: number;
  ISODateString: string;
  boolean: boolean;
  enum: string; // Enum values are represented as strings
};

/**
 * Converts a single ColumnDefinition to a key-value type.
 */
type ColumnFromDefinition<T extends ColumnDefinition> = T['type'] extends keyof ColumnTypeMap
  ? { [K in T['key']]: ColumnTypeMap[T['type']] }
  : never;

// Helper to merge union of objects into a single object type
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void
  ? I
  : never;

/**
 * Converts an array of ColumnDefinitions into a columns object type.
 * This utility type ensures that the columns property matches what columnDefinitions specifies.
 *
 * @example
 * const definitions: ColumnDefinition[] = [
 *   { key: "title", type: "string", name: "Title", description: "..." },
 *   { key: "duration", type: "number", name: "Duration", description: "..." }
 * ];
 * type Columns = ColumnsFromDefinitions<typeof definitions>;
 * // Results in: { title: string; duration: number; }
 */
export type ColumnsFromDefinitions<T extends readonly ColumnDefinition[] | ColumnDefinition[]> =
  UnionToIntersection<
    T[number] extends infer Def
      ? Def extends ColumnDefinition
        ? ColumnFromDefinition<Def>
        : never
      : never
  >;

/**
 * Entity information for YouTube videos.
 * @example
 * // From source [1]
 * interface YoutubeVideoEntity {
 *   extractor: "youtube";
 *   id: "erLbbextvlY";
 *   url: "https://youtu.be/erLbbextvlY";
 * }
 */
export interface YoutubeVideoEntity {
  extractor: 'youtube';
  id: string;
  url: string;
}

/**
 * Represents a single item within a YouTube playlist.
 * The columns type is dynamically derived from the columnDefinitions array.
 * @example
 * // From source [1]
 * interface PlaylistItem {
 *   entity: { extractor: "youtube", id: "erLbbextvlY", url: "https://youtu.be/erLbbextvlY" };
 *   columns: { title: "7 Days Stranded On An Island", duration: 1346, publishedAt: "2024-03-30T16:00:01Z", ... };
 * }
 */
export interface PlaylistItem<
  TColumnDefinitions extends readonly ColumnDefinition[] = ColumnDefinition[],
> {
  entity: YoutubeVideoEntity;
  columns: ColumnsFromDefinitions<TColumnDefinitions>;
}

/**
 * Data structure for a YouTube playlist response containing multiple video items.
 * The columns in each item are dynamically typed based on columnDefinitions.
 * @example
 * // From source [1]
 * interface YoutubeMultipleData {
 *   type: "multiple";
 *   data: {
 *     engine: "youtube:playlist";
 *     items: [...];
 *     columnDefinitions: [...];
 *   };
 * }
 */
export interface YoutubeMultipleData<
  TColumnDefinitions extends readonly ColumnDefinition[] = ColumnDefinition[],
> {
  type: 'multiple';
  data: {
    engine: 'youtube:playlist' | 'youtube:channel' | string; // Allow channels and other YouTube engine types
    items: PlaylistItem<TColumnDefinitions>[];
    columnDefinitions: TColumnDefinitions;
    metadata?: Record<string, unknown>; // Allow metadata to contain actual data (channels, playlists, etc.)
  };
}

/**
 * Data structure for a single YouTube video response.
 * The columns type is dynamically derived from the columnDefinitions array.
 * @example
 * // From source [3]
 * interface YoutubeSingleData {
 *   type: "single";
 *   data: {
 *     entity: { extractor: "youtube", id: "oSnOD9d2P8s", url: "https://www.youtube.com/watch?v=oSnOD9d2P8s" };
 *     columns: { publishedAt: "2025-05-20T15:17:25Z", duration: 2, ... };
 *     columnDefinitions: [...];
 *     metadata: {};
 *   };
 * }
 */
export interface YoutubeSingleData<
  TColumnDefinitions extends readonly ColumnDefinition[] = ColumnDefinition[],
> {
  type: 'single';
  data: {
    entity: YoutubeVideoEntity;
    columns: ColumnsFromDefinitions<TColumnDefinitions>;
    columnDefinitions: TColumnDefinitions;
    metadata: Record<string, never>; // Assuming metadata is an empty object
  };
}

/**
 * Entity information for Google Drive files.
 * @example
 * // From source [12]
 * interface GoogleDriveFileEntity {
 *   extractor: "GoogleDrive";
 *   id: "1KumtKmHsn7M3wn3iQsiVxR2ch2x3ZFRw";
 *   url: "https://drive.google.com/file/d/...";
 * }
 */
export interface GoogleDriveFileEntity {
  extractor: 'GoogleDrive';
  id: string;
  url: string;
}

/**
 * Data structure for a single Google Drive file response.
 * The columns type is dynamically derived from the columnDefinitions array.
 * @example
 * // From source [12]
 * interface GoogleDriveSingleData {
 *   type: "single";
 *   data: {
 *     entity: { extractor: "GoogleDrive", id: "...", url: "..." };
 *     columns: { duration: 501.699, thumbnail: "...", ... };
 *     columnDefinitions: [...];
 *     metadata: {};
 *   };
 * }
 */
export interface GoogleDriveSingleData<
  TColumnDefinitions extends readonly ColumnDefinition[] = ColumnDefinition[],
> {
  type: 'single';
  data: {
    entity: GoogleDriveFileEntity;
    columns: ColumnsFromDefinitions<TColumnDefinitions>;
    columnDefinitions: TColumnDefinitions;
    metadata: Record<string, never>;
  };
}

/**
 * Entity information for LinkedIn posts.
 * @example
 * // From source [18]
 * interface LinkedInPostEntity {
 *   extractor: "LinkedIn";
 *   id: "7392624389000847361";
 *   url: "https://www.linkedin.com/posts/prcary_massive-news-that-mrbeast-is-attending-activity-...";
 * }
 */
export interface LinkedInPostEntity {
  extractor: 'LinkedIn';
  id: string;
  url: string;
}

/**
 * Data structure for a single LinkedIn post response.
 * The columns type is dynamically derived from the columnDefinitions array.
 * @example
 * // From source [18]
 * interface LinkedInSingleData {
 *   type: "single";
 *   data: {
 *     entity: { extractor: "LinkedIn", id: "...", url: "..." };
 *     columns: { thumbnail: "...", isLive: false, title: "..." };
 *     columnDefinitions: [...];
 *     metadata: {};
 *   };
 * }
 */
export interface LinkedInSingleData<
  TColumnDefinitions extends readonly ColumnDefinition[] = ColumnDefinition[],
> {
  type: 'single';
  data: {
    entity: LinkedInPostEntity;
    columns: ColumnsFromDefinitions<TColumnDefinitions>;
    columnDefinitions: TColumnDefinitions;
    metadata: Record<string, never>;
  };
}

/**
 * Entity information for Facebook Ads.
 * @example
 * // From Facebook Ads Library response
 * interface FacebookAdsEntity {
 *   extractor: "facebook:ads";
 *   id: "2402341450181506";
 *   url: "https://video-otp1-1.xx.fbcdn.net/o1/v/t2/f2/m69/...";
 * }
 */
export interface FacebookAdsEntity {
  extractor: 'facebook:ads';
  id: string;
  url: string;
}

/**
 * Represents a single item within a Facebook Ads Library collection.
 * The columns type is dynamically derived from the columnDefinitions array.
 * @example
 * // From Facebook Ads Library response
 * interface FacebookAdsItem {
 *   entity: { extractor: "facebook:ads", id: "2402341450181506", url: "..." };
 *   columns: { title: "...", description: "...", thumbnail: "...", ... };
 * }
 */
export interface FacebookAdsItem<
  TColumnDefinitions extends readonly ColumnDefinition[] = ColumnDefinition[],
> {
  entity: FacebookAdsEntity;
  columns: ColumnsFromDefinitions<TColumnDefinitions>;
}

/**
 * Data structure for a Facebook Ads Library response containing multiple ad items.
 * The columns in each item are dynamically typed based on columnDefinitions.
 * @example
 * // From Facebook Ads Library response
 * interface FacebookAdsMultipleData {
 *   type: "multiple";
 *   data: {
 *     engine: "facebook:ads_library";
 *     items: [...];
 *     columnDefinitions: [...];
 *     metadata: { viewAllPageID: "...", ... };
 *   };
 * }
 */
export interface FacebookAdsMultipleData<
  TColumnDefinitions extends readonly ColumnDefinition[] = ColumnDefinition[],
> {
  type: 'multiple';
  data: {
    engine: 'facebook:ads_library';
    items: FacebookAdsItem<TColumnDefinitions>[];
    columnDefinitions: TColumnDefinitions;
    metadata?: Record<string, unknown>; // Contains search metadata like viewAllPageID, countries, etc.
  };
}

/**
 * Entity information for generic files.
 * @example
 * // From source [19], [20]
 * interface GenericFileEntity {
 *   extractor: "generic";
 *   id: "01d3ca4aaa8f47fd";
 *   url: "https://www.dropbox.com/scl/fi/pmlin1t49bxiu6yi1egsv/...";
 * }
 */
export interface GenericFileEntity {
  extractor: 'generic';
  id: string;
  url: string;
}

/**
 * Data structure for a single generic file response.
 * The columns type is dynamically derived from the columnDefinitions array.
 * @example
 * // From source [19]
 * interface GenericSingleData {
 *   type: "single";
 *   data: {
 *     entity: { extractor: "generic", id: "...", url: "..." };
 *     columns: { type: "video" };
 *     columnDefinitions: [...];
 *     metadata: {};
 *   };
 * }
 */
export interface GenericSingleData<
  TColumnDefinitions extends readonly ColumnDefinition[] = ColumnDefinition[],
> {
  type: 'single';
  data: {
    entity: GenericFileEntity;
    columns: ColumnsFromDefinitions<TColumnDefinitions>;
    columnDefinitions: TColumnDefinitions;
    metadata: Record<string, never>;
  };
}

/**
 * Union type for all possible successful data payloads returned by the API.
 */
export type AnyDataPayload =
  | YoutubeMultipleData
  | YoutubeSingleData
  | GoogleDriveSingleData
  | GoogleDriveFolderData
  | LinkedInSingleData
  | FacebookAdsMultipleData
  | GenericSingleData;

/**
 * The top-level type for any API response, encapsulating both success and error scenarios.
 *
 * @example
 * // Successful response for a YouTube playlist
 * const success: ApiResponse = {
 *   success: true,
 *   data: { type: "multiple", data: { engine: "youtube:playlist", items: [], columnDefinitions: [] } }
 * };
 *
 * @example
 * // Error response
 * const error: ApiResponse = {
 *   success: false,
 *   error: { statusCode: 412, error: "Precondition Failed", message: "error message", code: "external.media.error" },
 *   status: "error"
 * };
 */
export type ApiResponse = SuccessResponse<AnyDataPayload> | ErrorResponse;

/**
 * SSE event types from the unfurl endpoint
 */
interface SSEColumnDefinitionEvent {
  type: 'columnDefinition';
  columnDefinitions: ColumnDefinition[];
}

interface SSEEntitiesEvent {
  type: 'entities';
  entities: Array<{
    entity: YoutubeVideoEntity;
    columns: Record<string, unknown>;
  }>;
}

interface SSEMetadataEvent {
  // 'folder' is a Google Drive folder (see googleDriveFolder.ts); the rest are YouTube.
  type: 'channel' | 'playlist' | 'folder';
  id: string;
  title: string;
  description?: string;
  // Counts shown to users come from the actual extracted-item count, not this
  // field, so the wire's videoCount/fileCount aren't consumed (left untyped).
  videoCount?: number;
}

/**
 * Progress update data sent during SSE streaming.
 * Provides partial data as entities arrive.
 */
export interface UnfurlProgressData {
  /**
   * Column definitions (available after first event)
   */
  columnDefinitions: ColumnDefinition[] | null;
  /**
   * All entities received so far
   */
  entities: Array<{
    entity: YoutubeVideoEntity;
    columns: Record<string, unknown>;
  }>;
  /**
   * Metadata (available if/when received)
   */
  metadata: SSEMetadataEvent | null;
  /**
   * Whether the stream is complete
   */
  isComplete: boolean;
  /**
   * Number of entities loaded so far
   */
  loadedCount: number;
}

/**
 * Parses an SSE stream and accumulates events into a final AnyDataPayload.
 *
 * @param response - The fetch Response object from the SSE endpoint
 * @param signal - Optional AbortSignal to cancel the request
 * @param onProgress - Optional callback for progress updates as entities arrive
 * @returns Promise resolving to the accumulated unfurl data
 */
async function parseSSEStream(
  response: Response,
  signal?: AbortSignal,
  onProgress?: (progress: UnfurlProgressData) => void,
): Promise<AnyDataPayload> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  const acc: {
    columnDefinitions: ColumnDefinition[] | null;
    allEntities: Array<{ entity: YoutubeVideoEntity; columns: Record<string, unknown> }>;
    metadata: SSEMetadataEvent | null;
  } = {
    columnDefinitions: null,
    allEntities: [],
    metadata: null,
  };

  // SSE event state must persist across chunk boundaries
  let currentEvent: string | null = null;
  let currentData = '';
  let parseErrors = 0;
  const MAX_PARSE_ERRORS = 5; // Allow up to 5 parse errors before failing

  function applyParsedSSEEvent(
    parsed: { type: string },
    onProgressCb: ((progress: UnfurlProgressData) => void) | undefined,
  ): void {
    if (parsed.type === 'columnDefinition') {
      const event = parsed as SSEColumnDefinitionEvent;
      acc.columnDefinitions = event.columnDefinitions;
      if (onProgressCb) {
        onProgressCb({
          columnDefinitions: acc.columnDefinitions,
          entities: acc.allEntities,
          metadata: acc.metadata,
          isComplete: false,
          loadedCount: acc.allEntities.length,
        });
      }
    } else if (parsed.type === 'entities') {
      const event = parsed as SSEEntitiesEvent;
      acc.allEntities.push(...event.entities);
      if (onProgressCb) {
        onProgressCb({
          columnDefinitions: acc.columnDefinitions,
          entities: acc.allEntities,
          metadata: acc.metadata,
          isComplete: false,
          loadedCount: acc.allEntities.length,
        });
      }
    } else if (
      parsed.type === 'channel' ||
      parsed.type === 'playlist' ||
      parsed.type === 'folder'
    ) {
      acc.metadata = parsed as SSEMetadataEvent;
      if (onProgressCb) {
        onProgressCb({
          columnDefinitions: acc.columnDefinitions,
          entities: acc.allEntities,
          metadata: acc.metadata,
          isComplete: false,
          loadedCount: acc.allEntities.length,
        });
      }
    }
  }

  try {
    while (true) {
      // Check if aborted
      if (signal?.aborted) {
        reader.cancel();
        throw new Error('Request aborted');
      }

      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEvent = line.substring('event:'.length).trim();
        } else if (line.startsWith('data:')) {
          // SSE spec: multiple data lines are concatenated with newlines
          const dataContent = line.substring('data:'.length).trim();
          currentData = currentData ? currentData + '\n' + dataContent : dataContent;
        } else if (line === '' && currentEvent && currentData) {
          // Empty line marks end of event - process it.
          // `ping` is an SSE keepalive from the backend; its data is not JSON.
          if (currentEvent !== 'ping') {
            try {
              const parsed = JSON.parse(currentData);
              applyParsedSSEEvent(parsed, onProgress);
            } catch (e) {
              parseErrors++;
              console.error(
                `Failed to parse SSE event data (error ${parseErrors}/${MAX_PARSE_ERRORS}):`,
                currentData,
                e,
              );
              // If we've encountered too many parse errors, the stream is likely corrupted
              if (parseErrors >= MAX_PARSE_ERRORS) {
                throw new Error(
                  `Too many SSE parse errors (${parseErrors}). Stream may be corrupted.`,
                );
              }
            }
          }

          // Reset for next event
          currentEvent = null;
          currentData = '';
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  // Process any remaining buffered data after stream ends (reuse currentEvent/currentData
  // from outer scope — they may contain partial event data from the last chunk).
  // We only need to handle entities in the final buffer for now; metadata in the final
  // buffer is not needed yet.
  if (buffer.trim()) {
    const remainingLines = buffer.split('\n');

    for (const line of remainingLines) {
      if (line.startsWith('event:')) {
        currentEvent = line.substring('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        const dataContent = line.substring('data:'.length).trim();
        currentData = currentData ? currentData + '\n' + dataContent : dataContent;
      } else if (line === '' && currentEvent && currentData) {
        // `ping` is an SSE keepalive from the backend; its data is not JSON.
        if (currentEvent !== 'ping') {
          try {
            const parsed = JSON.parse(currentData);
            applyParsedSSEEvent(parsed, onProgress);
          } catch (e) {
            console.error('Failed to parse buffered SSE event:', e);
          }
        }
        currentEvent = null;
        currentData = '';
      }
    }

    // If we still have unparsed data, try parsing it as a final incomplete event
    if (currentData && currentEvent !== 'ping') {
      try {
        const parsed = JSON.parse(currentData);
        applyParsedSSEEvent(parsed, onProgress);
      } catch (e) {
        console.error('Failed to parse incomplete buffered event:', e);
      }
    }
  }

  // Call final progress callback to indicate completion
  if (onProgress) {
    onProgress({
      columnDefinitions: acc.columnDefinitions,
      entities: acc.allEntities,
      metadata: acc.metadata,
      isComplete: true,
      loadedCount: acc.allEntities.length,
    });
  }

  // Build final AnyDataPayload from accumulated data
  if (!acc.columnDefinitions) {
    throw new Error('No column definitions received from SSE stream');
  }

  // Google Drive folder — delegate to its own builder (separate parse branch).
  // Folders always emit a folder metadata event, so a single-file folder still
  // routes here rather than to the single-entity path below.
  if (isGoogleDriveFolderMetadata(acc.metadata)) {
    return buildGoogleDriveFolderData(
      acc.columnDefinitions,
      acc.allEntities,
      acc.metadata as GoogleDriveFolderMetadata,
    );
  }

  // If we have multiple entities or metadata, it's a multiple data response
  if (acc.allEntities.length > 1 || acc.metadata) {
    // TODO: Add Facebook Ads support to SSE parser when SSE streaming is implemented for Facebook Ads Library
    // Currently, SSE only handles YouTube data (youtube:channel, youtube:playlist)
    // Facebook Ads data goes through a different path (direct JSON response)
    const engine = acc.metadata?.type === 'channel' ? 'youtube:channel' : 'youtube:playlist';

    return {
      type: 'multiple',
      data: {
        engine,
        items: acc.allEntities,
        columnDefinitions: acc.columnDefinitions,
        metadata: acc.metadata || {},
      },
    } as YoutubeMultipleData;
  }

  // Single entity
  if (acc.allEntities.length === 1) {
    const item = acc.allEntities[0];
    return {
      type: 'single',
      data: {
        entity: item.entity,
        columns: item.columns,
        columnDefinitions: acc.columnDefinitions,
        metadata: {},
      },
    } as YoutubeSingleData;
  }

  throw new Error('No entities received from SSE stream');
}

/**
 * Unfurls a URL to extract metadata and information about the external media.
 * Uses Server-Sent Events (SSE) to stream results progressively.
 *
 * @param url - The URL to unfurl
 * @param signal - Optional AbortSignal to cancel the request
 * @param onProgress - Optional callback to receive progress updates as entities arrive
 * @returns A promise that resolves to the unfurled data payload
 * @throws {ApiError} If the request fails or the URL cannot be unfurled
 *
 * @example
 * // Basic usage
 * const data = await unfurlURL('https://www.youtube.com/watch?v=abc123');
 * if (data.type === 'single') {
 *   console.log(data.data.entity.url);
 * }
 *
 * @example
 * // With progress updates
 * const data = await unfurlURL(
 *   'https://www.youtube.com/@kurzgesagt',
 *   undefined,
 *   (progress) => {
 *     console.log(`Loaded ${progress.loadedCount} videos...`);
 *   }
 * );
 */
export async function unfurlURL(
  url: string,
  signal?: AbortSignal,
  onProgress?: (progress: UnfurlProgressData) => void,
): Promise<AnyDataPayload> {
  // Use apiSSE for consistent auth handling and SSE-specific headers
  // See apiSSE documentation for why SSE uses JSON instead of msgpack
  const response = await apiSSE('/library/media/external/unfurl/sse', {
    method: 'POST',
    body: { url: safeEncodeURI(url) },
    signal,
  });

  // Parse the SSE stream
  // Note: apiSSE() already handles errors and throws ApiError, so no manual error handling needed
  return await parseSSEStream(response, signal, onProgress);
}

// ============================================================================
// Phase 2: Type-Safe Helpers and Type Guards
// ============================================================================

/**
 * Type-safe column access helper.
 * Uses columnDefinitions to ensure type safety when accessing column values.
 *
 * @param columns - The columns object from unfurl data
 * @param columnDefinitions - Array of column definitions
 * @param key - The column key to access
 * @returns The column value, typed based on the column definition, or undefined if not found
 *
 * @example
 * const title = getColumn(columns, columnDefinitions, 'title'); // string | undefined
 * const duration = getColumn(columns, columnDefinitions, 'duration'); // number | undefined
 */
export function getColumn(
  columns: Record<string, unknown>,
  columnDefinitions: readonly ColumnDefinition[],
  key: string,
): unknown {
  const columnDef = columnDefinitions.find((def) => def.key === key);
  if (!columnDef) {
    return undefined;
  }

  const value = columns[key];
  if (value === undefined || value === null) {
    return undefined;
  }

  // Return the value - type checking should be done by the caller based on columnDef.type
  return value;
}

/**
 * Type-safe column access helper with default value.
 * Uses columnDefinitions to ensure type safety when accessing column values.
 *
 * @param columns - The columns object from unfurl data
 * @param columnDefinitions - Array of column definitions
 * @param key - The column key to access
 * @param defaultValue - Default value to return if column is missing
 * @returns The column value or the default value
 *
 * @example
 * const duration = getColumnWithDefault(columns, columnDefinitions, 'duration', 0); // number
 * const isLive = getColumnWithDefault(columns, columnDefinitions, 'isLive', false); // boolean
 */
export function getColumnWithDefault<T>(
  columns: Record<string, unknown>,
  columnDefinitions: readonly ColumnDefinition[],
  key: string,
  defaultValue: T,
): T {
  const value = getColumn(columns, columnDefinitions, key);
  if (value !== undefined && value !== null) {
    return value as T;
  }
  return defaultValue;
}

/**
 * Type guard to check if data is YoutubeMultipleData (playlist or channel).
 */
export function isYoutubeMultipleData(data: AnyDataPayload): data is YoutubeMultipleData {
  return (
    data.type === 'multiple' &&
    'engine' in data.data &&
    typeof data.data.engine === 'string' &&
    data.data.engine.startsWith('youtube:')
  );
}

/**
 * Type guard to check if data is YoutubeSingleData.
 */
export function isYoutubeSingleData(data: AnyDataPayload): data is YoutubeSingleData {
  return data.type === 'single' && data.data.entity.extractor === 'youtube';
}

/**
 * Type guard to check if data is GoogleDriveSingleData.
 */
export function isGoogleDriveSingleData(data: AnyDataPayload): data is GoogleDriveSingleData {
  return data.type === 'single' && data.data.entity.extractor === 'GoogleDrive';
}

/**
 * Type guard to check if data is LinkedInSingleData.
 */
export function isLinkedInSingleData(data: AnyDataPayload): data is LinkedInSingleData {
  return data.type === 'single' && data.data.entity.extractor === 'LinkedIn';
}

/**
 * Type guard to check if data is GenericSingleData.
 */
export function isGenericSingleData(data: AnyDataPayload): data is GenericSingleData {
  return data.type === 'single' && data.data.entity.extractor === 'generic';
}

/**
 * Type guard to check if data is FacebookAdsMultipleData.
 */
export function isFacebookAdsMultipleData(data: AnyDataPayload): data is FacebookAdsMultipleData {
  return (
    data.type === 'multiple' &&
    'engine' in data.data &&
    typeof data.data.engine === 'string' &&
    data.data.engine === 'facebook:ads_library'
  );
}

/**
 * Type guard to check if data is a single item (any single data type).
 */
export function isSingleData(
  data: AnyDataPayload,
): data is YoutubeSingleData | GoogleDriveSingleData | LinkedInSingleData | GenericSingleData {
  return data.type === 'single';
}

/**
 * Type guard to check if data is multiple items (YoutubeMultipleData or FacebookAdsMultipleData).
 */
export function isMultipleData(
  data: AnyDataPayload,
): data is YoutubeMultipleData | FacebookAdsMultipleData {
  return data.type === 'multiple';
}

/**
 * Type guard to check if YoutubeMultipleData is a channel.
 */
export function isYoutubeChannel(data: YoutubeMultipleData): boolean {
  return data.data.engine === 'youtube:channel' || data.data.engine?.includes('channel') === true;
}

/**
 * Type guard to check if YoutubeMultipleData is a playlist.
 */
export function isYoutubePlaylist(data: YoutubeMultipleData): boolean {
  return (
    data.data.engine === 'youtube:playlist' ||
    (data.data.engine?.includes('playlist') === true && !isYoutubeChannel(data))
  );
}

/**
 * Helper to extract common fields from single data items.
 * Provides a type-safe way to access commonly used columns.
 */
export interface CommonSingleDataFields {
  title: string;
  duration: number;
  thumbnail: string;
  isLive: boolean;
}

/**
 * Extracts common fields from a single data item using type-safe column access.
 *
 * @param data - Single unfurl data (YoutubeSingleData, GoogleDriveSingleData, etc.)
 * @returns Common fields with defaults applied
 */
export function getCommonSingleDataFields(
  data: YoutubeSingleData | GoogleDriveSingleData | LinkedInSingleData | GenericSingleData,
): CommonSingleDataFields {
  const { columns, columnDefinitions } = data.data;

  const title = getColumnWithDefault(columns, columnDefinitions, 'title', '');
  const duration = getColumnWithDefault(columns, columnDefinitions, 'duration', 0);
  const thumbnail = getColumnWithDefault(columns, columnDefinitions, 'thumbnail', '');

  // Check both 'isLive' and 'is_live' for compatibility
  const isLive =
    getColumnWithDefault(columns, columnDefinitions, 'isLive', false) ||
    getColumnWithDefault(columns, columnDefinitions, 'is_live', false);

  return {
    title: typeof title === 'string' ? title : '',
    duration: typeof duration === 'number' ? duration : 0,
    thumbnail: typeof thumbnail === 'string' ? thumbnail : '',
    isLive: typeof isLive === 'boolean' ? isLive : false,
  };
}

/**
 * Drop-in replacement for ChooseMediaItem that supports both:
 * - Direct property access (for backward compatibility and performance)
 * - Dynamic columns from unfurl data (for flexibility and completeness)
 *
 * This type preserves all API metadata while maintaining compatibility with
 * existing code that accesses properties directly (e.g., `item.title`, `item.duration`).
 *
 * Properties are stored directly for fast access, but the full API response
 * is also preserved in `columns` for type-safe access to any metadata.
 *
 * @example
 * // Direct property access (backward compatible)
 * const title = item.title;
 * const duration = item.duration;
 *
 * // Dynamic column access (for new/unknown fields)
 * const customField = getColumn(item.columns, item.columnDefinitions, 'customField');
 */
export interface UnfurlItem {
  // ============================================
  // Core Identity (always required)
  // ============================================
  id: string;
  url: string;

  // ============================================
  // ChooseMediaItem compatibility properties
  // (stored directly for performance + compatibility)
  // ============================================
  title: string;
  duration?: number;
  thumbnail?: string;
  description?: string;
  size?: number; // 0 for external media, actual size for file uploads
  type?: string; // 'video' for external media, MIME type for files
  defaultPosition?: number; // Position in playlist/order (0-based index)

  // Duplicate detection
  uniqueId?: string; // Computed hash for duplicate detection

  // User metadata
  userMetadata?: Record<string, any>; // User-provided metadata

  // YouTube-specific (optional, from YouTubePlaylistItem)
  channel?: {
    id: string;
    name: string;
  };
  publishedAt?: string; // ISO date string
  isShorts?: boolean; // Whether this is a YouTube Short
  isLive?: boolean; // Whether this is a live stream
  extractor?: string; // 'youtube', 'GoogleDrive', 'LinkedIn', 'generic', etc.

  // ============================================
  // Dynamic columns from unfurl data
  // (preserves all API metadata)
  // ============================================
  /**
   * Dynamic columns from the unfurl API response.
   * Contains all metadata provided by the API, including fields
   * that may not have direct property equivalents.
   *
   * Use getColumn() or getColumnWithDefault() helper for type-safe access.
   *
   * @example
   * const customField = getColumn(item.columns, item.columnDefinitions, 'customField');
   */
  columns: Record<string, unknown>;

  /**
   * Column definitions that describe the structure and types
   * of the columns object. Used for type-safe column access.
   *
   * This array defines what columns are available and their types,
   * enabling type-safe access via getColumn() helper functions.
   */
  columnDefinitions: ColumnDefinition[];
}

/**
 * Converts AnyDataPayload to UnfurlItem[] format.
 * - For YoutubeMultipleData: converts PlaylistItem[] to UnfurlItem[]
 * - For FacebookAdsMultipleData: converts FacebookAdsItem[] to UnfurlItem[]
 * - For single data: creates a single-item array from the data
 *
 * This function transforms raw unfurl data into processed items ready for UI consumption.
 * Returns UnfurlItem objects that preserve all API metadata while maintaining backward
 * compatibility with ChooseMediaItem usage patterns.
 *
 * Terminology:
 * - `unfurlData` (input): Raw AnyDataPayload from the unfurl API endpoint
 * - `unfurledItems` (output): Processed UnfurlItem[] ready for display/selection
 *
 * @param data - Any unfurl data payload (unfurlData)
 * @param originalUrl - The pasted URL, which a single item keeps as its url unless its
 *   platform posts the unfurl entity's url (`addsUnfurlItemUrl`)
 * @returns Array of UnfurlItem objects with both direct properties and dynamic columns
 */
export function convertUnfurlDataToUnfurlItem(
  data: AnyDataPayload,
  originalUrl: string,
): UnfurlItem[] {
  if (data.type === 'multiple') {
    // For multiple data (playlists/channels), convert items to UnfurlItem format
    const { items, columnDefinitions } = data.data;
    return items.map((item, index) => {
      const { entity, columns } = item;
      const title = getColumnWithDefault(columns, columnDefinitions, 'title', '');
      // Use getColumn for duration to allow undefined when column doesn't exist (e.g., Facebook Ads)
      const durationValue = getColumn(columns, columnDefinitions, 'duration');
      const duration = typeof durationValue === 'number' ? durationValue : undefined;
      const thumbnail = getColumnWithDefault(columns, columnDefinitions, 'thumbnail', '');
      const descriptionValue = getColumn(columns, columnDefinitions, 'description');
      const description = typeof descriptionValue === 'string' ? descriptionValue : undefined;
      const publishedAtValue = getColumn(columns, columnDefinitions, 'publishedAt');
      const publishedAt =
        typeof publishedAtValue === 'string' && publishedAtValue ? publishedAtValue : undefined;

      // The API uses "isShorts" (plural, camelCase) as the column key
      // Use getColumn to detect when the column is missing (returns undefined)
      const isShortsValue = getColumn(columns, columnDefinitions, 'isShorts');
      const isShorts = typeof isShortsValue === 'boolean' ? isShortsValue : undefined;

      // Extract isLive column
      const isLiveValue = getColumn(columns, columnDefinitions, 'isLive');
      const isLive = typeof isLiveValue === 'boolean' ? isLiveValue : undefined;

      // Extract channel information if available
      const channelIdValue = getColumn(columns, columnDefinitions, 'channelId');
      const channelNameValue = getColumn(columns, columnDefinitions, 'channel');
      const channel =
        typeof channelIdValue === 'string' && typeof channelNameValue === 'string'
          ? {
              id: channelIdValue,
              name: channelNameValue,
            }
          : undefined;

      // Prefer the real MIME type when the source provides one (Google Drive
      // folders expose a `mimeType` column for each file → image/* , audio/* ,
      // video/*), matching the file-upload convention of storing the MIME
      // string in `type`. YouTube has no mimeType column → falls back to 'video'.
      const mimeTypeValue = getColumn(columns, columnDefinitions, 'mimeType');
      const type = typeof mimeTypeValue === 'string' && mimeTypeValue ? mimeTypeValue : 'video';

      return {
        // Core identity
        id: entity.id,
        url: entity.url,

        // ChooseMediaItem compatibility properties
        title: typeof title === 'string' ? title : '',
        duration,
        thumbnail: typeof thumbnail === 'string' ? thumbnail : undefined,
        description,
        size: 0, // External media doesn't have file size
        type, // MIME type for Drive files; 'video' default for YouTube/others
        defaultPosition: index, // Position in playlist/order (0-based)

        // YouTube-specific properties
        channel,
        publishedAt,
        isShorts, // undefined if column is missing, boolean if present
        isLive, // undefined if column is missing, boolean if present
        extractor: entity.extractor,

        // Dynamic columns from unfurl data (preserves all API metadata)
        columns: { ...columns }, // Preserve full columns object
        columnDefinitions, // Include column definitions for type-safe access
      };
    });
  } else {
    // For single data, create a single-item array
    const { entity, columns, columnDefinitions } = data.data;
    const commonFields = getCommonSingleDataFields(data);
    const descriptionValue = getColumn(columns, columnDefinitions, 'description');
    const description = typeof descriptionValue === 'string' ? descriptionValue : undefined;
    const publishedAtValue = getColumn(columns, columnDefinitions, 'publishedAt');
    const publishedAt =
      typeof publishedAtValue === 'string' && publishedAtValue ? publishedAtValue : undefined;

    // The API uses "isShorts" (plural, camelCase) as the column key
    // Use getColumn to detect when the column is missing (returns undefined)
    const isShortsValue = getColumn(columns, columnDefinitions, 'isShorts');
    const isShorts = typeof isShortsValue === 'boolean' ? isShortsValue : undefined;

    // Extract isLive column
    const isLiveValue = getColumn(columns, columnDefinitions, 'isLive');
    const isLive = typeof isLiveValue === 'boolean' ? isLiveValue : undefined;

    // Extract channel information if available
    const channelIdValue = getColumn(columns, columnDefinitions, 'channelId');
    const channelNameValue = getColumn(columns, columnDefinitions, 'channel');
    const channel =
      typeof channelIdValue === 'string' && typeof channelNameValue === 'string'
        ? {
            id: channelIdValue,
            name: channelNameValue,
          }
        : undefined;

    // The single-video unfurl's entity url is the acquisition URL for most platforms (Google
    // Drive's download link), so the pasted link stays the handle; share platforms hand back a
    // re-resolvable link per file instead, which is what adding posts (see `addsUnfurlItemUrl`).
    const url = getPlatformConfig(entity.extractor).addsUnfurlItemUrl
      ? entity.url
      : originalUrl || entity.url;

    return [
      {
        // Core identity
        id: entity.id,
        url,

        // ChooseMediaItem compatibility properties
        title: commonFields.title,
        duration: commonFields.duration || undefined,
        thumbnail: commonFields.thumbnail || undefined,
        description: typeof description === 'string' ? description : undefined,
        size: 0, // External media doesn't have file size
        type: 'video', // Default type for external media

        // YouTube-specific properties
        channel,
        publishedAt: typeof publishedAt === 'string' && publishedAt ? publishedAt : undefined,
        isShorts, // undefined if column is missing, boolean if present
        isLive, // undefined if not available, boolean if present
        extractor: entity.extractor,

        // Dynamic columns from unfurl data (preserves all API metadata)
        columns: { ...columns }, // Preserve full columns object
        columnDefinitions, // Include column definitions for type-safe access
      },
    ];
  }
}

/**
 * Gets the collection name from AnyDataPayload.
 * - For channels: returns channel name
 * - For playlists: returns playlist title
 * - For Facebook Ads Library: returns "Facebook Ads Library" with optional page ID
 * - For single items: returns item title
 *
 * @param data - Any unfurl data payload
 * @param defaultName - Default name to return if no name found
 * @returns Collection name
 */
export function getCollectionNameFromUnfurlData(data: AnyDataPayload, defaultName: string): string {
  if (data.type === 'multiple') {
    const { metadata, columnDefinitions, items } = data.data;
    const metadataObj = metadata as Record<string, unknown> | undefined;

    // Check if it's a Facebook Ads Library
    if (isFacebookAdsMultipleData(data)) {
      const viewAllPageID = metadataObj?.viewAllPageID as string | undefined;
      if (viewAllPageID) {
        return `Facebook Ads Library (Page: ${viewAllPageID})`;
      }
      return 'Facebook Ads Library';
    }

    // Check if it's a channel
    if (isYoutubeMultipleData(data) && isYoutubeChannel(data)) {
      const channelName =
        (metadataObj?.name as string | undefined) ??
        (items[0] ? getColumnWithDefault(items[0].columns, columnDefinitions, 'channel', '') : '');
      return typeof channelName === 'string' && channelName ? channelName : defaultName;
    }

    // Playlists and Google Drive folders both name the collection via metadata.title.
    const playlistTitle = (metadataObj?.title as string | undefined) ?? '';
    return typeof playlistTitle === 'string' && playlistTitle ? playlistTitle : defaultName;
  } else {
    // For single items, use the title
    const commonFields = getCommonSingleDataFields(data);
    return commonFields.title || defaultName;
  }
}

/**
 * Gets the collection type from AnyDataPayload.
 *
 * @param data - Any unfurl data payload
 * @returns 'playlist' | 'channel' | 'ads_library' | 'single' | null
 */
export function getCollectionTypeFromUnfurlData(
  data: AnyDataPayload,
): 'playlist' | 'channel' | 'ads_library' | 'single' | null {
  if (data.type === 'multiple') {
    if (isFacebookAdsMultipleData(data)) {
      return 'ads_library';
    }
    if (isYoutubeMultipleData(data) && isYoutubeChannel(data)) {
      return 'channel';
    }
    if (isYoutubeMultipleData(data) && isYoutubePlaylist(data)) {
      return 'playlist';
    }
    // Google Drive folders are 'multiple' too; they intentionally fall through
    // to null here for now (v1 reuses the playlist picker, no folder-specific
    // collection label).
    return null;
  }
  return 'single';
}

// ============================================================================
// Phase 6: Adapter Functions Removed
// ============================================================================
// All adapter functions (unfurlToURLPeekData, unfurlToYoutubeUnwindData, unfurlToURLInfo)
// and their helper functions have been removed as part of the migration to use
// AnyDataPayload directly. New code should use AnyDataPayload types and the type-safe
// helper functions (getColumn, getColumnWithDefault, etc.) defined above.
